const QUICK_ORDER_DEFAULT_API_BASE = 'https://alb.deliver3d.net:3000';
const QUICK_ORDER_UPLOAD_ENDPOINT = '/export/upload-image';
const QUICK_ORDER_TOKEN_KEY = 'deliver3d_guest_token';
const QUICK_ORDER_MAX_BYTES = 50 * 1024 * 1024;
const QUICK_ORDER_MIN_DIMENSION = 200;
const QUICK_ORDER_MAX_DIMENSION = 8096;
const QUICK_ORDER_MAX_SIDE = 1280;
const QUICK_ORDER_TARGET_BYTES = 100 * 1024;

class QuickOrderUpload extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.form = document.getElementById(this.dataset.productFormId || '');
    this.productForm = this.form?.closest('product-form-component');
    this.variants = this.#parseVariants();
    this.slots = Array.from(this.querySelectorAll('[data-upload-slot]'));
    this.abortControllers = new Map();

    this.slots.forEach((slot) => {
      slot.querySelector('[data-upload-input]')?.addEventListener('change', (event) => this.#handleFile(event, slot));
      slot.querySelector('[data-upload-remove]')?.addEventListener('click', () => this.#clearSlot(slot));
    });

    document.addEventListener('variant:update', this.#handleVariantUpdate);
    this.form?.addEventListener('submit', this.#handleSubmit, true);
    this.#syncVariant(this.form?.querySelector('[name="id"]')?.value);
    this.#syncGate();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.#handleVariantUpdate);
    this.form?.removeEventListener('submit', this.#handleSubmit, true);
    this.abortControllers.forEach((controller) => controller.abort());
  }

  #parseVariants() {
    try {
      return JSON.parse(this.querySelector('[data-quick-order-variants]')?.textContent || '{}');
    } catch (error) {
      console.error('[QuickOrderUpload] Invalid variant configuration', error);
      return {};
    }
  }

  #handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId || '') !== String(this.dataset.productId || '')) return;
    this.#syncVariant(event.detail?.resource?.id);
    queueMicrotask(() => this.#syncGate());
  };

  #handleSubmit = (event) => {
    if (this.#isReady()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const missingSlot = this.slots.find((slot) => !slot.hidden && !this.#getUrlInput(slot)?.value);
    this.#setStatus(missingSlot, 'Upload this photo before adding the product to your cart.');
    missingSlot?.querySelector('[data-upload-input]')?.focus();
  };

  #syncVariant(variantId) {
    const requiresSecond = this.#isTruthy(this.variants[String(variantId || '')]);
    const secondSlot = this.slots[1];
    const secondUrl = this.querySelector('[data-upload-url="2"]');

    if (secondSlot) secondSlot.hidden = !requiresSecond;
    if (secondUrl) secondUrl.disabled = !requiresSecond;
    if (!requiresSecond && secondSlot) this.#clearSlot(secondSlot);
    this.#syncGate();
  }

  #isTruthy(value) {
    if (value === true || value === 1) return true;
    return ['true', 'yes', '1', 'on'].includes(String(value ?? '').trim().toLowerCase());
  }

  async #handleFile(event, slot) {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    this.#clearSlot(slot, false);
    const controller = new AbortController();
    this.abortControllers.set(slot, controller);
    this.#setLoading(slot, true);

    try {
      const processedFile = await this.#prepareFile(file);
      const token = await this.#getToken();
      if (!token) throw new Error('Unable to initialize upload access. Refresh the page and try again.');

      const body = new FormData();
      body.append('image', processedFile);
      const apiBase = window.THEME_CONFIG?.apiBaseUrl || QUICK_ORDER_DEFAULT_API_BASE;
      const response = await fetch(`${String(apiBase).replace(/\/$/, '')}${QUICK_ORDER_UPLOAD_ENDPOINT}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
        signal: controller.signal,
      });

      let result;
      try {
        result = await response.json();
      } catch (error) {
        throw new Error('The upload service returned an invalid response.');
      }

      if (result?.data?.moderation?.limittype != null) {
        throw new Error('This image cannot be used. Please choose a different photo.');
      }
      if (!response.ok || result?.success === false) {
        throw new Error(result?.message || 'Upload request failed.');
      }

      const url = result?.data?.fileUrl || result?.url || result?.imageUrl || result?.image_url;
      if (!url) throw new Error('The upload response did not include an image URL.');
      this.#completeSlot(slot, url);
    } catch (error) {
      if (error?.name !== 'AbortError') this.#setStatus(slot, error?.message || 'Upload failed. Please try again.');
      input.value = '';
    } finally {
      if (this.abortControllers.get(slot) === controller) this.abortControllers.delete(slot);
      this.#setLoading(slot, false);
      this.#syncGate();
    }
  }

  async #prepareFile(file) {
    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();
    const heifBrand = await this.#detectHeifBrand(file);
    if (file.size > QUICK_ORDER_MAX_BYTES) throw new Error('The image must be 50 MB or smaller.');
    if (heifBrand.isHeif || name.endsWith('.heif') || name.endsWith('.heifs') || type === 'image/heif') {
      throw new Error('HEIF is not supported. Upload HEIC, JPG, PNG or WEBP.');
    }
    if (!/\.(jpe?g|png|webp|heic)$/i.test(name) && !['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(type)) {
      throw new Error('Upload a JPG, PNG, WEBP or HEIC image.');
    }

    let source = file;
    if (heifBrand.isHeic) source = await this.#convertHeic(file);
    const image = await this.#loadImage(source);
    if (image.naturalWidth < QUICK_ORDER_MIN_DIMENSION || image.naturalHeight < QUICK_ORDER_MIN_DIMENSION) {
      throw new Error('Image width and height must both be at least 200 px.');
    }
    if (image.naturalWidth > QUICK_ORDER_MAX_DIMENSION || image.naturalHeight > QUICK_ORDER_MAX_DIMENSION) {
      throw new Error('Image width and height must both be 8096 px or smaller.');
    }
    return this.#compressToJpeg(image, source.name);
  }

  async #detectHeifBrand(file) {
    const bytes = new Uint8Array(await file.slice(0, 32).arrayBuffer());
    if (bytes.length < 12) return { isHeic: false, isHeif: false };

    const boxType = String.fromCharCode(...bytes.slice(4, 8));
    if (boxType !== 'ftyp') return { isHeic: false, isHeif: false };

    const brands = String.fromCharCode(...bytes.slice(8));
    const isHeic = /heic|heix|hevc|hevx/.test(brands);
    return {
      isHeic,
      isHeif: !isHeic && /heif|heifs|mif1|msf1/.test(brands),
    };
  }

  async #convertHeic(file) {
    if (typeof window.HeicTo === 'undefined') {
      await new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-quick-order-heic]');
        if (existing) {
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }
        const script = document.createElement('script');
        script.dataset.quickOrderHeic = 'true';
        script.src = 'https://cdn.jsdelivr.net/npm/heic-to@1.3.0/dist/iife/heic-to.js';
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', reject, { once: true });
        document.head.appendChild(script);
      });
    }
    const blob = await window.HeicTo({ blob: file, type: 'image/jpeg', quality: 0.95 });
    return new File([blob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
  }

  #loadImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('The selected image could not be read.'));
      };
      image.src = url;
    });
  }

  async #compressToJpeg(image, originalName) {
    let width = image.naturalWidth;
    let height = image.naturalHeight;
    const scale = Math.min(1, QUICK_ORDER_MAX_SIDE / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Image processing is unavailable in this browser.');

    let blob;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      canvas.width = width;
      canvas.height = height;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(image, 0, 0, width, height);
      let low = 0.05;
      let high = 0.92;
      for (let step = 0; step < 10; step += 1) {
        const quality = (low + high) / 2;
        const candidate = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
        if (!candidate) break;
        blob = candidate;
        if (candidate.size <= QUICK_ORDER_TARGET_BYTES) low = quality;
        else high = quality;
      }
      if (blob?.size <= QUICK_ORDER_TARGET_BYTES || Math.min(width, height) <= 320) break;
      width = Math.max(320, Math.round(width * 0.85));
      height = Math.max(320, Math.round(height * 0.85));
    }
    if (!blob) throw new Error('The image could not be processed.');
    return new File([blob], originalName.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
  }

  async #getToken() {
    const readToken = () => {
      try {
        return localStorage.getItem(QUICK_ORDER_TOKEN_KEY) || window.guestToken || '';
      } catch (error) {
        return window.guestToken || '';
      }
    };
    let token = readToken();
    if (token) return token;
    const result = await window.GuestAccount?.init?.();
    token = result?.token || readToken();
    return token;
  }

  #completeSlot(slot, url) {
    const preview = slot.querySelector('[data-upload-preview]');
    const urlInput = this.#getUrlInput(slot);
    if (preview) preview.src = url;
    if (urlInput) urlInput.value = url;
    slot.classList.add('is-complete');
    slot.querySelector('[data-upload-remove]')?.removeAttribute('hidden');
    this.#setStatus(slot, '');
  }

  #clearSlot(slot, clearInput = true) {
    this.abortControllers.get(slot)?.abort();
    this.abortControllers.delete(slot);
    const input = slot.querySelector('[data-upload-input]');
    const preview = slot.querySelector('[data-upload-preview]');
    const urlInput = this.#getUrlInput(slot);
    if (clearInput && input) input.value = '';
    if (preview?.src?.startsWith('blob:')) URL.revokeObjectURL(preview.src);
    if (preview) preview.removeAttribute('src');
    if (urlInput) urlInput.value = '';
    slot.classList.remove('is-complete');
    slot.querySelector('[data-upload-remove]')?.setAttribute('hidden', '');
    this.#setStatus(slot, '');
    this.#syncGate();
  }

  #getUrlInput(slot) {
    return this.querySelector(`[data-upload-url="${slot.dataset.uploadSlot}"]`);
  }

  #setLoading(slot, loading) {
    slot.querySelector('[data-upload-progress]')?.toggleAttribute('hidden', !loading);
    const input = slot.querySelector('[data-upload-input]');
    if (input) input.disabled = loading;
  }

  #setStatus(slot, message) {
    const status = slot?.querySelector('[data-upload-status]');
    if (status) status.textContent = message;
  }

  #isReady() {
    return this.slots.every((slot) => slot.hidden || Boolean(this.#getUrlInput(slot)?.value));
  }

  #syncGate() {
    const ready = this.#isReady();
    const button = this.productForm?.querySelector('[ref="addToCartButton"]');
    const canAddToCart = button?.dataset.casecaseCanAddToCart !== 'false';
    if (button) button.disabled = !ready || !canAddToCart;
    this.productForm?.toggleAttribute('data-quick-order-ready', ready);
  }
}

if (!customElements.get('quick-order-upload')) customElements.define('quick-order-upload', QuickOrderUpload);
